"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold transition-all duration-100 cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive select-none",
  {
    variants: {
      variant: {
        default:
          "border border-amber-500 bg-amber-400 text-gray-900 shadow-[0_4px_0_#d97706] hover:bg-amber-500 hover:text-gray-900 hover:-translate-y-px hover:shadow-[0_5px_0_#d97706] active:translate-y-[2px] active:shadow-[0_1px_0_#b45309]",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-gray-300 bg-white text-gray-700 shadow-[0_3px_0_#d1d5db] hover:bg-gray-50 hover:text-gray-800 hover:-translate-y-px hover:shadow-[0_4px_0_#d1d5db] active:translate-y-[2px] active:shadow-[0_1px_0_#d1d5db]",
        secondary:
          "border border-gray-200 bg-gray-100 text-gray-700 shadow-[0_3px_0_#e5e7eb] hover:bg-gray-200 hover:text-gray-800 hover:-translate-y-px hover:shadow-[0_4px_0_#e5e7eb] active:translate-y-[2px] active:shadow-[0_1px_0_#e5e7eb]",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        brutalist:
          "border border-amber-500 bg-amber-400 text-gray-900 shadow-[0_4px_0_#d97706] hover:bg-amber-500 hover:text-gray-900 hover:-translate-y-px hover:shadow-[0_5px_0_#d97706] active:translate-y-[2px] active:shadow-[0_1px_0_#b45309]",
        brutalistOutline:
          "border border-gray-300 bg-white text-gray-700 shadow-[0_3px_0_#d1d5db] hover:bg-gray-50 hover:text-gray-800 hover:-translate-y-px hover:shadow-[0_4px_0_#d1d5db] active:translate-y-[2px] active:shadow-[0_1px_0_#d1d5db]",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 text-xs has-[>svg]:px-2.5",
        lg: "h-11 px-6 has-[>svg]:px-4",
        xl: "h-12 px-8 text-base has-[>svg]:px-6",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
